import { Document, Packer, Paragraph, TextRun, Table, TableCell, TableRow, WidthType, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';

/**
 * Generate báo cáo đề nghị sản xuất biển số xe
 * @param {Object} data - Dữ liệu báo cáo
 * @param {string} data.unitName - Tên đơn vị
 * @param {string} data.provinceCode - Mã tỉnh (49)
 * @param {string} data.series - Seri (A, B, AB...)
 * @param {Array} data.plates - Danh sách biển số
 * @param {string} data.color - Màu nền (Trắng/Vàng/Xanh)
 * @param {string} data.unitType - Loại đơn vị (Biển/Bộ/...)
 * @param {boolean} data.isBatch - Có phải biển số dãy không (có từ số-đến số)
 * @returns {Promise<{blob: Blob, filename: string}>} - Trả về blob và tên file
 */
export const generateLicensePlateReport = async (data) => {
  const { unitName, provinceCode, series, plates, color, unitType, isBatch = false } = data;
  
  const now = new Date();
  const day = now.getDate();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();
  
  // Tính từ số đến số
  const numbers = plates.map(p => {
    const match = p.fullPlate.match(/\d{3}\.\d{2}/);
    return match ? match[0] : '';
  }).filter(n => n);
  
  // Sort numbers để đảm bảo thứ tự đúng
  numbers.sort((a, b) => {
    const numA = parseFloat(a.replace('.', ''));
    const numB = parseFloat(b.replace('.', ''));
    return numA - numB;
  });
  
  // Chỉ hiển thị "từ số - đến số" cho biển số dãy
  let fromNumber = '';
  let toNumber = '';
  let fromToText = '';
  
  if (isBatch && numbers.length > 0) {
    fromNumber = numbers[0];
    toNumber = numbers[numbers.length - 1];
    fromToText = `${fromNumber} – ${toNumber}`;
  }
  
  // Danh sách biển số chi tiết
  const platesList = numbers.join('; ');
  
  const doc = new Document({
    sections: [{
      properties: {
        page: {
          margin: {
            top: 1134,      // 2cm = 2 * 567 = 1134 twips
            bottom: 1134,   // 2cm = 2 * 567 = 1134 twips
            left: 1701,     // 3cm = 3 * 567 = 1701 twips
            right: 850,     // 1.5cm = 1.5 * 567 = 850.5 ≈ 850 twips
            gutter: 0       // 0cm = 0 twips
          }
        }
      },
      children: [
        // Header - Dùng Table để tạo 2 cột (trái và phải)
        new Table({
          width: {
            size: 100,
            type: WidthType.PERCENTAGE
          },
          borders: {
            top: { style: 'none', size: 0, color: 'FFFFFF' },
            bottom: { style: 'none', size: 0, color: 'FFFFFF' },
            left: { style: 'none', size: 0, color: 'FFFFFF' },
            right: { style: 'none', size: 0, color: 'FFFFFF' },
            insideHorizontal: { style: 'none', size: 0, color: 'FFFFFF' },
            insideVertical: { style: 'none', size: 0, color: 'FFFFFF' }
          },
          rows: [
            new TableRow({
              children: [
                // Cột trái
                new TableCell({
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: 'CÔNG AN TỈNH LÂM ĐỒNG',
                          bold: false,
                          size: 26
                        })
                      ],
                      alignment: AlignmentType.CENTER
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `CÔNG AN ${unitName}`,
                          bold: true,
                          size: 26,
                          underline: {}
                        })
                      ],
                      alignment: AlignmentType.CENTER,
                      spacing: { after: 200 }
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: 'Số: ',
                          size: 26
                        }),
                        new TextRun({
                          text: '/CAP',
                          size: 26
                        })
                      ],
                      alignment: AlignmentType.LEFT,
                      spacing: { after: 100 }
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: 'V/v đặt hàng sản xuất biển số xe phản quang',
                          italics: true,
                          size: 26
                        })
                      ],
                      alignment: AlignmentType.LEFT
                    })
                  ],
                  width: { size: 50, type: WidthType.PERCENTAGE },
                  borders: {
                    top: { style: 'none', size: 0 },
                    bottom: { style: 'none', size: 0 },
                    left: { style: 'none', size: 0 },
                    right: { style: 'none', size: 0 }
                  }
                }),
                // Cột phải
                new TableCell({
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: 'CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM',
                          bold: true,
                          size: 26
                        })
                      ],
                      alignment: AlignmentType.CENTER
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: 'Độc lập - Tự do - Hạnh phúc',
                          bold: true,
                          size: 26,
                          underline: {}
                        })
                      ],
                      alignment: AlignmentType.CENTER,
                      spacing: { after: 200 }
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `${unitName}, ngày ${day} tháng ${month} năm ${year}`,
                          italics: true,
                          size: 26
                        })
                      ],
                      alignment: AlignmentType.CENTER
                    })
                  ],
                  width: { size: 50, type: WidthType.PERCENTAGE },
                  borders: {
                    top: { style: 'none', size: 0 },
                    bottom: { style: 'none', size: 0 },
                    left: { style: 'none', size: 0 },
                    right: { style: 'none', size: 0 }
                  }
                })
              ]
            })
          ]
        }),
        
        // Spacing sau header
        new Paragraph({
          text: '',
          spacing: { after: 400 }
        }),
        
        // Kính gửi
        new Paragraph({
          children: [
            new TextRun({
              text: 'Kính gửi: Phòng PC08 Công an tỉnh Lâm Đồng',
              bold: true,
              size: 26
            })
          ],
          alignment: AlignmentType.CENTER,
          spacing: { after: 300 }
        }),
        
        // Nội dung
        new Paragraph({
          children: [
            new TextRun({
              text: 'Căn cứ theo dõi số lượng phương tiện đăng ký thực tế sử dụng tại đơn vị;',
              size: 26
            })
          ],
          spacing: { after: 200 }
        }),
        
        new Paragraph({
          children: [
            new TextRun({
              text: `Công an ${unitName} đề nghị Phòng PC08 Công an tỉnh Lâm Đồng tập hợp đề nghị sản xuất biển số xe cơ giới như sau:`,
              size: 26
            })
          ],
          spacing: { after: 400 }
        }),
        
        // Tiêu đề bảng
        new Paragraph({
          children: [
            new TextRun({
              text: 'I. MÔ TÔ',
              bold: true,
              size: 26
            })
          ],
          spacing: { after: 200 }
        }),
        
        new Paragraph({
          children: [
            new TextRun({
              text: '1. Seri',
              bold: true,
              size: 26
            })
          ],
          spacing: { after: 200 }
        }),
        
        // Bảng - Khác nhau giữa biển số lẻ và biển số dãy
        new Table({
          width: {
            size: 100,
            type: WidthType.PERCENTAGE
          },
          rows: isBatch ? [
            // BIỂN SỐ DÃY - Có cột "Từ số đến số"
            // Header row
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({ text: 'Đơn vị', alignment: AlignmentType.CENTER })],
                  width: { size: 15, type: WidthType.PERCENTAGE }
                }),
                new TableCell({
                  children: [new Paragraph({ text: 'Đầu biển theo tỉnh', alignment: AlignmentType.CENTER })],
                  width: { size: 10, type: WidthType.PERCENTAGE }
                }),
                new TableCell({
                  children: [new Paragraph({ text: 'Seri', alignment: AlignmentType.CENTER })],
                  width: { size: 8, type: WidthType.PERCENTAGE }
                }),
                new TableCell({
                  children: [new Paragraph({ text: 'Từ số đến số', alignment: AlignmentType.CENTER })],
                  width: { size: 12, type: WidthType.PERCENTAGE }
                }),
                new TableCell({
                  children: [new Paragraph({ text: 'Biển số', alignment: AlignmentType.CENTER })],
                  width: { size: 35, type: WidthType.PERCENTAGE }
                }),
                new TableCell({
                  children: [new Paragraph({ text: 'Nền', alignment: AlignmentType.CENTER })],
                  width: { size: 8, type: WidthType.PERCENTAGE }
                }),
                new TableCell({
                  children: [new Paragraph({ text: 'SL', alignment: AlignmentType.CENTER })],
                  width: { size: 6, type: WidthType.PERCENTAGE }
                }),
                new TableCell({
                  children: [new Paragraph({ text: 'ĐV', alignment: AlignmentType.CENTER })],
                  width: { size: 6, type: WidthType.PERCENTAGE }
                })
              ]
            }),
            // Data row
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({ text: unitName, alignment: AlignmentType.CENTER })]
                }),
                new TableCell({
                  children: [new Paragraph({ text: provinceCode, alignment: AlignmentType.CENTER })]
                }),
                new TableCell({
                  children: [new Paragraph({ text: series, alignment: AlignmentType.CENTER })]
                }),
                new TableCell({
                  children: [new Paragraph({ text: fromToText, alignment: AlignmentType.CENTER })]
                }),
                new TableCell({
                  children: [new Paragraph({ text: platesList })]
                }),
                new TableCell({
                  children: [new Paragraph({ text: color, alignment: AlignmentType.CENTER })]
                }),
                new TableCell({
                  children: [new Paragraph({ text: plates.length.toString(), alignment: AlignmentType.CENTER })]
                }),
                new TableCell({
                  children: [new Paragraph({ text: unitType, alignment: AlignmentType.CENTER })]
                })
              ]
            }),
            // Tổng row
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({ text: 'TỔNG', alignment: AlignmentType.CENTER, bold: true })],
                  columnSpan: 6
                }),
                new TableCell({
                  children: [new Paragraph({ text: plates.length.toString(), alignment: AlignmentType.CENTER, bold: true })]
                }),
                new TableCell({
                  children: [new Paragraph({ text: '', alignment: AlignmentType.CENTER })]
                })
              ]
            })
          ] : [
            // BIỂN SỐ LẺ - KHÔNG có cột "Từ số đến số"
            // Header row
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({ text: 'Đơn vị', alignment: AlignmentType.CENTER })],
                  width: { size: 15, type: WidthType.PERCENTAGE }
                }),
                new TableCell({
                  children: [new Paragraph({ text: 'Đầu biển theo tỉnh', alignment: AlignmentType.CENTER })],
                  width: { size: 10, type: WidthType.PERCENTAGE }
                }),
                new TableCell({
                  children: [new Paragraph({ text: 'Seri', alignment: AlignmentType.CENTER })],
                  width: { size: 10, type: WidthType.PERCENTAGE }
                }),
                new TableCell({
                  children: [new Paragraph({ text: 'Biển số', alignment: AlignmentType.CENTER })],
                  width: { size: 45, type: WidthType.PERCENTAGE }
                }),
                new TableCell({
                  children: [new Paragraph({ text: 'Nền', alignment: AlignmentType.CENTER })],
                  width: { size: 8, type: WidthType.PERCENTAGE }
                }),
                new TableCell({
                  children: [new Paragraph({ text: 'SL', alignment: AlignmentType.CENTER })],
                  width: { size: 6, type: WidthType.PERCENTAGE }
                }),
                new TableCell({
                  children: [new Paragraph({ text: 'ĐV', alignment: AlignmentType.CENTER })],
                  width: { size: 6, type: WidthType.PERCENTAGE }
                })
              ]
            }),
            // Data row
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({ text: unitName, alignment: AlignmentType.CENTER })]
                }),
                new TableCell({
                  children: [new Paragraph({ text: provinceCode, alignment: AlignmentType.CENTER })]
                }),
                new TableCell({
                  children: [new Paragraph({ text: series, alignment: AlignmentType.CENTER })]
                }),
                new TableCell({
                  children: [new Paragraph({ text: platesList })]
                }),
                new TableCell({
                  children: [new Paragraph({ text: color, alignment: AlignmentType.CENTER })]
                }),
                new TableCell({
                  children: [new Paragraph({ text: plates.length.toString(), alignment: AlignmentType.CENTER })]
                }),
                new TableCell({
                  children: [new Paragraph({ text: unitType, alignment: AlignmentType.CENTER })]
                })
              ]
            }),
            // Tổng row
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({ text: 'TỔNG', alignment: AlignmentType.CENTER, bold: true })],
                  columnSpan: 5
                }),
                new TableCell({
                  children: [new Paragraph({ text: plates.length.toString(), alignment: AlignmentType.CENTER, bold: true })]
                }),
                new TableCell({
                  children: [new Paragraph({ text: '', alignment: AlignmentType.CENTER })]
                })
              ]
            })
          ]
        }),
        
        // Kết luận
        new Paragraph({
          children: [
            new TextRun({
              text: 'Kính đề xuất các đồng chí quan tâm sản xuất biển số xe trên kịp thời./.', 
              size: 26
            })
          ],
          spacing: { before: 400, after: 600 }
        }),
        
        // Layout 2 cột: Nơi nhận (trái) và Chữ ký (phải)
        new Table({
          width: {
            size: 100,
            type: WidthType.PERCENTAGE
          },
          borders: {
            top: { style: 'none', size: 0, color: 'FFFFFF' },
            bottom: { style: 'none', size: 0, color: 'FFFFFF' },
            left: { style: 'none', size: 0, color: 'FFFFFF' },
            right: { style: 'none', size: 0, color: 'FFFFFF' },
            insideHorizontal: { style: 'none', size: 0, color: 'FFFFFF' },
            insideVertical: { style: 'none', size: 0, color: 'FFFFFF' }
          },
          rows: [
            new TableRow({
              children: [
                // Cột trái - Nơi nhận
                new TableCell({
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: 'Nơi nhận:',
                          bold: true,
                          size: 24
                        })
                      ]
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: '- Như trên;',
                          size: 24
                        })
                      ]
                    }),
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: '- Lưu: CAP.',
                          size: 24
                        })
                      ]
                    })
                  ],
                  width: { size: 50, type: WidthType.PERCENTAGE },
                  borders: {
                    top: { style: 'none', size: 0 },
                    bottom: { style: 'none', size: 0 },
                    left: { style: 'none', size: 0 },
                    right: { style: 'none', size: 0 }
                  },
                  verticalAlign: 'top'
                }),
                // Cột phải - Chữ ký
                new TableCell({
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: 'P. TRƯỞNG CÔNG AN',
                          bold: true,
                          size: 26
                        })
                      ],
                      alignment: AlignmentType.CENTER
                    }),
                    new Paragraph({
                      text: '',
                      spacing: { after: 1200 } // Khoảng trống để ký tên
                    })
                  ],
                  width: { size: 50, type: WidthType.PERCENTAGE },
                  borders: {
                    top: { style: 'none', size: 0 },
                    bottom: { style: 'none', size: 0 },
                    left: { style: 'none', size: 0 },
                    right: { style: 'none', size: 0 }
                  },
                  verticalAlign: 'top'
                })
              ]
            })
          ]
        })
      ]
    }]
  });
  
  // Generate blob và tên file
  const blob = await Packer.toBlob(doc);
  const fileName = `Báo cáo CSGT - ${unitName} - ${Date.now()}.docx`;
  
  return { blob, fileName };
};
